import { useGetUsersList } from "@birdwatch/client";

import { UserRole } from "@/types/openapi-enum";

import { toUserFilterOption } from "../filter-options/user-filter-option";
import type { FilterOptionsResult } from "../types";
import { useDebouncedSearch } from "./use-debounced-search";

const PAGE_SIZE = 20;

/**
 * User picker options for filter fields — debounced server search + infinite
 * paging over the given role(s), with `selectedOptions` resolved by ids so chips
 * label users who are not on the current page. One home for every user picker.
 *
 * `roles` is a comma list (`"MANAGER,TECHNICIAN"`), matching the wire format of
 * the `roles` filter. The `{ in: … }` operator objects are load-bearing: the SDK
 * shorthand downgrades a single value to `eq`, which the in-only list endpoint
 * ignores — returning unfiltered users and a wrong chip label.
 *
 * `take(100)` on the selected query rather than the page size: a chip holding
 * more selected ids than one page would otherwise lose their avatars and labels.
 */
export function useUserFilterOptions(
  roles: string,
  ctx?: { selectedValues?: string[] },
): FilterOptionsResult {
  const selectedValues = ctx?.selectedValues ?? [];
  const { query, onSearch } = useDebouncedSearch();

  const listResult = useGetUsersList()
    .search(query || undefined)
    .filters({ roles: { in: roles } })
    .take(PAGE_SIZE)
    .sort("name")
    .getInfinite();

  const selectedResult = useGetUsersList()
    .filters({ roles: { in: roles }, ids: { in: selectedValues.join(",") } })
    .take(100)
    .get({ enabled: selectedValues.length > 0 });

  const users = listResult.data?.pages.flatMap((page) => page.data) ?? [];
  const selected = selectedValues.length ? (selectedResult.data ?? []) : [];

  return {
    options: users.map(toUserFilterOption),
    selectedOptions: selected.map(toUserFilterOption),
    isLoading: listResult.isLoading,
    onSearch,
    hasMore: listResult.hasNextPage,
    fetchMore: listResult.fetchNextPage,
  };
}

/** The household-member picker — users whose role is `MEMBER`. */
export const useMemberFilterOptions = (ctx?: { selectedValues?: string[] }) =>
  useUserFilterOptions(UserRole.Member, ctx);
